import React, { useCallback, useEffect, useState } from "react";

import { Typography } from "@packages/ds";
import momentjs from "moment";
import { useIntl } from "react-intl";
import { Link } from "react-router-dom";

import { APP_NAME } from "Constants/documentationUrls";
import * as Accordion from "ds/Accordion";
import Loading from "ds/PageLoading";
import { selectSubscribeFlowEnabled } from "Reducers/featureFlags/featureFlags.selectors";
import {
  organizationAddressDataSelector,
  getOrganizationAddress
} from "Reducers/organization/address";
import { useOrganizationProfile } from "Reducers/organization/hooks/useOrganizationProfile";
import { organizationPaymentSourceSelector } from "Reducers/organization/paymentSource/paymentSource.selectors";
import { getOrganizationPaymentSource } from "Reducers/organization/paymentSource/thunks/getOrganizationPaymentSource.thunk";
import { getOrganizationPrepayment } from "Reducers/organization/prepayment";
import { getOrganizationProfile } from "Reducers/organization/profile";
import { selectOrganizationSubscriptions } from "Reducers/organization/subscription";
import AddressForm from "src/organization/pages/billing/details/components/AddressForm";
import OrderDetailSummary from "src/organization/pages/billing/details/components/OrderDetailSummary";
import PaymentElements from "src/organization/pages/billing/details/components/PaymentElements/PaymentElements";
import { Prepayment } from "src/organization/pages/billing/details/components/PrepaymentTransactions/PrepaymentTransactions";
import TrialWidget from "src/organization/pages/billing/details/components/TrialWidget";
import { useAppDispatch, useAppSelector } from "Store/hooks";
import SubscribeImage from "Vendor_resources/images/subscribe.svg";

import * as S from "./Billing.styles";

import type { Organization } from "@packages/client";
import type { OrganizationAddress } from "Api/organizations/address/types";

const Billing = ({ organization }: { organization: Organization }) => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const paymentSource = useAppSelector(state =>
    organizationPaymentSourceSelector(state, {
      organizationId: organization.name
    })
  );
  const subscribeFlowEnabled = useAppSelector(selectSubscribeFlowEnabled);
  const existingPaymentSourceType = paymentSource?.type;

  const [addressData, setAddressData] = useState<OrganizationAddress>();
  const [draftAddress, setDraftAddress] = useState({
    ...(addressData ?? {}),
    vat_number: "",
    company_name: "",
    billing_contact: ""
  });
  const [shouldRefreshPaymentType, refreshPaymentType] = useState(false);
  const [hasBillingAddress, setHasBillingAddress] = useState(false);
  const [openAccordionItems, setOpenAccordionItems] = useState(
    hasBillingAddress
      ? ["billing_details", "payment_method"]
      : ["billing_details"]
  );

  const orgSubscriptions = useAppSelector(state =>
    selectOrganizationSubscriptions(state, {
      organizationId: organization.name
    })
  );

  const { loadingGet } = useAppSelector(state =>
    organizationAddressDataSelector(state, { organizationId: organization.id })
  );

  const { organizationProfile } = useOrganizationProfile(organization.name);

  const prepayment = useAppSelector(state => {
    if (organization) {
      return state.organizationPrepayment.data[organization.id];
    }
  });

  useEffect(() => {
    dispatch(getOrganizationPrepayment({ organizationId: organization.id }));
  }, [dispatch, organization.id]);

  const getAddress = useCallback(async () => {
    const result = await dispatch(
      getOrganizationAddress({ organizationId: organization.id })
    );

    const addressResponse = result.payload;

    setAddressData(addressResponse);
    setHasBillingAddress(!!addressResponse?.name_line);

    if (addressResponse?.name_line) {
      setOpenAccordionItems(["billing_details", "payment_method"]);
    }
  }, [dispatch, organization.id]);

  useEffect(() => {
    getAddress();
  }, [getAddress]);

  useEffect(() => {
    dispatch(
      getOrganizationPaymentSource({ organizationId: organization.name })
    );
  }, [dispatch, organization]);

  const hasPaymentSource = !!paymentSource?.type;
  const shouldDisableCountry =
    hasPaymentSource ||
    (hasPaymentSource && !organizationProfile?.current_trial?.active);
  const loadedAddress = { ...addressData };

  const onAddressSubmit = async () => {
    await getAddress();
    setHasBillingAddress(true);
    setOpenAccordionItems(["payment_method"]);
    dispatch(getOrganizationProfile({ organizationId: organization.name }));
    refreshPaymentType(!shouldRefreshPaymentType);
  };

  const shouldShowSubscribePage = !hasBillingAddress && subscribeFlowEnabled;

  return (
    <S.BillingDetailsContainer>
      <S.Aside>
        <S.InvoicesSection>
          <S.HeaderLayout>
            <Typography.Title tag="h3">
              {intl.formatMessage({ id: "upcoming_invoice" })}
            </Typography.Title>
            <Link to={`/${organization.name}/-/billing/history`}>
              {intl.formatMessage({ id: "view_all" })}
            </Link>
          </S.HeaderLayout>
          <S.SideSectionBox>
            <OrderDetailSummary
              organizationDescriptionId={organization.name}
              organizationId={organization.id}
            />
          </S.SideSectionBox>

          {prepayment && prepayment.balance.amount > 0 && (
            <S.SideSectionBox>
              <S.SectionBoxTitleWrapper>
                <strong>
                  {intl.formatMessage({
                    id: "billing.details.prepaid_funds_remaining"
                  })}
                </strong>
                <Typography.BodyText $color="granite" size="small">
                  {intl.formatMessage(
                    { id: "updated_moment" },
                    {
                      moment: momentjs(prepayment.last_updated_at).format(
                        "MMM D, YYYY"
                      )
                    }
                  )}
                </Typography.BodyText>
              </S.SectionBoxTitleWrapper>
              <Typography.Title tag="h2">
                {prepayment.balance.formatted}
              </Typography.Title>
              <Typography.BodyText size="small">
                {intl.formatMessage({
                  id: "billing.details.add_funds_message"
                })}
              </Typography.BodyText>
            </S.SideSectionBox>
          )}

          <TrialWidget
            hasPaymentSource={hasPaymentSource}
            currentTrial={organizationProfile?.current_trial}
            orgSubscriptions={orgSubscriptions}
          />
        </S.InvoicesSection>
      </S.Aside>

      {loadingGet ? (
        <S.LoadingWrapper>
          <Loading />
        </S.LoadingWrapper>
      ) : shouldShowSubscribePage ? (
        <S.Content>
          <S.HeaderLayout>
            <Typography.Title tag="h3">
              {intl.formatMessage({ id: "billing_details" })}
            </Typography.Title>
          </S.HeaderLayout>
          <S.SectionBox>
            <S.NoDetailsBox>
              <S.Illustration>
                <img src={SubscribeImage} alt="no billing details" />
              </S.Illustration>
              <S.SubscribeTitle tag="h3">
                {intl.formatMessage(
                  { id: "billing.subscribe.title" },
                  { app_name: APP_NAME }
                )}
              </S.SubscribeTitle>
              <S.SubscribeDescription>
                {intl.formatMessage({ id: "billing.subscribe.description" })}
              </S.SubscribeDescription>
              <S.SubscribeButton
                variant="primary"
                to={`/${organization.name}/-/subscribe`}
              >
                {intl.formatMessage({
                  id: "organization.billing.subscribe.btn.subscribe"
                })}
              </S.SubscribeButton>
              <S.TermsInfo weight="semiBold">
                {intl.formatMessage(
                  { id: "billing.terms_conditions" },
                  {
                    link: chunks => (
                      <S.TermsLink
                        analyticId="links.faq"
                        variant="link"
                        to={intl.formatMessage({ id: "links.faq" })}
                      >
                        {chunks}
                      </S.TermsLink>
                    )
                  }
                )}
              </S.TermsInfo>
            </S.NoDetailsBox>
          </S.SectionBox>
        </S.Content>
      ) : (
        <S.Content>
          <S.HeaderLayout>
            <Typography.Title tag="h3">
              {intl.formatMessage({ id: "billing_details" })}
            </Typography.Title>
          </S.HeaderLayout>
          <S.SectionBox>
            <Accordion.Root
              type="multiple"
              value={openAccordionItems}
              onValueChange={a => {
                setOpenAccordionItems(a);
              }}
            >
              <Accordion.Item value="billing_details">
                <Accordion.SectionHeader>
                  {intl.formatMessage({ id: "billing_details" })}
                </Accordion.SectionHeader>
                <Accordion.Content>
                  <S.Form>
                    <AddressForm
                      finalize={onAddressSubmit}
                      organizationId={organization.name}
                      addressData={addressData}
                      disableCountry={shouldDisableCountry}
                      loadedAddress={loadedAddress}
                      onEmailChange={() => {}}
                      onAddressChange={address => {
                        setAddressData(address);
                        setDraftAddress(address);
                      }}
                      draftAddress={draftAddress}
                      onDraftAddressChange={setDraftAddress}
                    />
                  </S.Form>
                </Accordion.Content>
              </Accordion.Item>

              {existingPaymentSourceType !== "invoice_account" && (
                <S.PaymentMethodsSection>
                  <Accordion.Item
                    value="payment_method"
                    disabled={!hasBillingAddress}
                  >
                    <Accordion.SectionHeader disabled={!hasBillingAddress}>
                      <S.HeaderLayoutChangePayment>
                        <S.SectionHeader>
                          {intl.formatMessage({ id: "payment_method" })}
                        </S.SectionHeader>
                      </S.HeaderLayoutChangePayment>
                    </Accordion.SectionHeader>
                    <Accordion.Content>
                      <PaymentElements
                        addressData={addressData}
                        organizationId={organization.id}
                        organizationDescriptionId={organization.name}
                        country={addressData?.country}
                      />
                    </Accordion.Content>
                    {!hasBillingAddress &&
                      intl.formatMessage({
                        id: "billing.details.payment_method.note"
                      })}
                  </Accordion.Item>
                </S.PaymentMethodsSection>
              )}
              {prepayment && (
                <Prepayment
                  organization={organization}
                  prepayment={prepayment}
                />
              )}
            </Accordion.Root>
          </S.SectionBox>
        </S.Content>
      )}
    </S.BillingDetailsContainer>
  );
};

export default Billing;
